import type { AppConfig, Entity, PatientStatus, Profile, Region, SubProfile } from '../types'
import type { Coords } from '../geo'

// ---------------------------------------------------------------------------
// Booking flow state machine. The spec mandates capabilities, not a specific
// screen order (§7); this models the canonical progression:
//   service → [sub-profile] → patient type → [region] → slot → details → done
// Conditional steps (sub-profile, region) are skipped automatically.
// ---------------------------------------------------------------------------

export type Step =
  | 'service'
  | 'subProfile'
  | 'patientType'
  | 'region'
  | 'slots'
  | 'details'
  | 'confirmation'

export interface SelectedSlot {
  entity: Entity
  office_id: number
  doctor_id: number | null
  date: string
  time: string
}

export interface BookingState {
  step: Step
  history: Step[]
  profile: Profile | null
  subProfile: SubProfile | null
  status: PatientStatus | null
  region: Region | null
  zip: string | null // 5-digit ZIP entered in the location step (§2.3)
  coords: Coords | null
  slotStart: string | null // retained date window for the slot picker (§3.3)
  slot: SelectedSlot | null
  confirmationId: string | null
}

export const initialState: BookingState = {
  step: 'service',
  history: [],
  profile: null,
  subProfile: null,
  status: null,
  region: null,
  zip: null,
  coords: null,
  slotStart: null,
  slot: null,
  confirmationId: null
}

export type Action =
  | { type: 'SELECT_PROFILE'; profile: Profile }
  | { type: 'SELECT_SUB_PROFILE'; subProfile: SubProfile }
  | { type: 'SELECT_STATUS'; status: PatientStatus }
  | { type: 'SELECT_REGION'; region: Region }
  | { type: 'SUBMIT_ZIP'; zip: string }
  | { type: 'USE_LOCATION'; coords: Coords }
  | { type: 'SET_SLOT_START'; date: string }
  | { type: 'SELECT_SLOT'; slot: SelectedSlot }
  | { type: 'BOOKED'; id: string }
  | { type: 'BACK' }
  | { type: 'EDIT_STEP'; step: Step }
  | { type: 'PRESELECT'; patch: Partial<BookingState> }
  | { type: 'RESTORE'; state: BookingState }
  | { type: 'RESTART' }

// ---- Pure helpers -------------------------------------------------------------

export function isTelemed(profile: Profile | null, subProfile: SubProfile | null): boolean {
  return !!(subProfile?.is_telemed || profile?.is_telemed)
}

/**
 * Whether results are booked by individual provider (headshot, credentials,
 * badge) rather than by clinic/office. Defaults to the company setting, but a
 * profile may override it — e.g. behavioral-health services (Therapy, Med
 * Management) offered inside an otherwise clinic-based primary-care flow.
 */
export function isProviderBased(config: AppConfig, profile: Profile | null): boolean {
  return (profile?.scheduling ?? config.company.scheduling) === 'provider'
}

/** Whether the "new patient" option must be hidden (§7.3). */
export function existingOnly(profile: Profile | null, subProfile: SubProfile | null): boolean {
  return !!(subProfile?.is_existing_only || profile?.is_existing_only)
}

export function hasSubProfiles(profile: Profile | null): boolean {
  return !!(profile?.sub_profiles && profile.sub_profiles.length > 0)
}

/**
 * Whether the slot-picking step is skipped. After-hours ("See a Provider Now")
 * jumps straight from patient type to the patient-info fields (§6.1) — there is
 * no slot to choose; the visit is started immediately.
 */
export function slotsSkipped(profile: Profile | null): boolean {
  return !!profile?.after_hours
}

/**
 * Whether the location/region step is skipped. It doesn't apply for:
 *  - after-hours profiles (no slot query is made; the BFF pins the office
 *    via after_hours_office_id),
 *  - virtual / telehealth visits when they are region-agnostic — provider
 *    rosters (behavioral health) or a clinic brand with a designated
 *    `virtual: 1` region to absorb the query. Clinic-based telemed without
 *    one (Total Men's Telemedicine) still asks the patient for a region,
 *    since availability is drawn from that region's offices,
 *  - a single region with no sub-regions (nothing to choose).
 */
export function regionSkipped(
  config: AppConfig,
  profile: Profile | null,
  subProfile: SubProfile | null
): boolean {
  // After-hours first: the after-hours profile is typically also is_telemed.
  if (profile?.after_hours) {
    return true
  }
  if (
    isTelemed(profile, subProfile) &&
    (isProviderBased(config, profile) || config.regions.some((r) => r.virtual === 1))
  ) {
    return true
  }
  // Clinic-based telemed without a virtual region falls through: the patient
  // picks a region like any other visit — including the single-region skip.
  if (config.regions.length === 1 && !config.regions[0].sub_regions?.length) {
    return true
  }
  return false
}

/** The region auto-assigned when the region step is skipped (virtual visits get the virtual region if configured). */
export function skippedRegion(
  config: AppConfig,
  profile: Profile | null,
  subProfile: SubProfile | null
): Region | null {
  if (profile?.after_hours) {
    return config.regions.find((r) => r.virtual === 1) || config.regions[0] || null
  }
  if (isTelemed(profile, subProfile)) {
    // Prefer the virtual region; fall back to the first region so the
    // availability query still carries a region id (the backend rejects
    // region-less queries; telemed rosters ignore which one).
    return config.regions.find((r) => r.virtual === 1) || config.regions[0] || null
  }
  if (config.regions.length === 1 && !config.regions[0].sub_regions?.length) {
    return config.regions[0]
  }
  return null
}

/** Whether a step already has its selection (slots/details always need interaction). */
export function stepFilled(state: BookingState, step: Step): boolean {
  switch (step) {
    case 'service':
      return !!state.profile
    case 'subProfile':
      return !!state.subProfile
    case 'patientType':
      return !!state.status
    case 'region':
      return !!state.region || !!state.zip || !!state.coords
    case 'slots':
      return !!state.slot
    default:
      return false
  }
}

/** The ordered steps that apply for the current selections (excludes confirmation). */
export function flowSteps(config: AppConfig, state: BookingState): Step[] {
  const steps: Step[] = ['service']
  if (hasSubProfiles(state.profile)) steps.push('subProfile')
  steps.push('patientType')
  if (!regionSkipped(config, state.profile, state.subProfile)) steps.push('region')
  if (!slotsSkipped(state.profile)) steps.push('slots')
  steps.push('details')
  return steps
}

/**
 * Given the current state, compute the next step after `from`, skipping any
 * conditional steps that don't apply. `config` resolves single-region auto-skip.
 */
function nextStep(state: BookingState, config: AppConfig, from: Step): Step {
  switch (from) {
    case 'service':
      if (hasSubProfiles(state.profile)) return 'subProfile'
      return 'patientType'
    case 'subProfile':
      return 'patientType'
    case 'patientType': {
      // Region is skipped for virtual visits, after-hours, or a single region;
      // slots are skipped for after-hours (straight to patient info — §6.1).
      if (!regionSkipped(config, state.profile, state.subProfile)) return 'region'
      return slotsSkipped(state.profile) ? 'details' : 'slots'
    }
    case 'region':
      return slotsSkipped(state.profile) ? 'details' : 'slots'
    case 'slots':
      return 'details'
    case 'details':
      return 'confirmation'
    case 'confirmation':
      return 'confirmation'
  }
}

function advance(state: BookingState, config: AppConfig, from: Step, patch: Partial<BookingState>): BookingState {
  let next = nextStep(state, config, from)
  let working: BookingState = { ...state, ...patch }

  // Auto-resolve region when the region step is skipped (null for virtual visits).
  if (next === 'slots' && from === 'patientType' && !working.region) {
    working = { ...working, region: skippedRegion(config, working.profile, working.subProfile) }
  }

  return { ...working, step: next, history: [...state.history, from] }
}

// ---- Reducer ------------------------------------------------------------------

export function makeReducer(config: AppConfig) {
  return function reducer(state: BookingState, action: Action): BookingState {
    switch (action.type) {
      case 'SELECT_PROFILE': {
        // Switching service resets all dependent selections (§7.8).
        const fresh: BookingState = {
          ...initialState,
          coords: state.coords, // geolocation persists across the flow
          profile: action.profile
        }
        return advance(fresh, config, 'service', {})
      }

      case 'SELECT_SUB_PROFILE': {
        const sp = action.subProfile
        // Existing-only sub-profile chosen while "new": reset downstream (§7.8).
        const resetStatus = sp.is_existing_only && state.status === 'new'
        return advance(
          state,
          config,
          'subProfile',
          {
            subProfile: sp,
            status: resetStatus ? null : state.status,
            region: resetStatus ? null : state.region,
            zip: resetStatus ? null : state.zip,
            slot: resetStatus ? null : state.slot
          }
        )
      }

      case 'SELECT_STATUS':
        return advance(state, config, 'patientType', { status: action.status })

      case 'SELECT_REGION':
        // Choosing a region clears any prior ZIP/coords location filter (§2.2).
        return advance(state, config, 'region', { region: action.region, zip: null })

      case 'SUBMIT_ZIP':
        // Search by ZIP instead of a named region: region-agnostic, zip sent to
        // the API (only when 5 digits — enforced by the caller — §2.3).
        return advance(state, config, 'region', { zip: action.zip, region: null, coords: null })

      case 'USE_LOCATION':
        // "Locate me": always re-query fresh coords (§1.5); region-agnostic search.
        return advance(state, config, 'region', { coords: action.coords, region: null, zip: null })

      case 'SET_SLOT_START':
        // Persist the slot-picker date window so it survives region/edit changes (§3.3).
        return { ...state, slotStart: action.date }

      case 'SELECT_SLOT':
        return advance(state, config, 'slots', { slot: action.slot })

      case 'BOOKED':
        return { ...state, step: 'confirmation', confirmationId: action.id }

      case 'BACK': {
        if (state.history.length === 0) return state
        const history = state.history.slice()
        const prev = history.pop()!
        return { ...state, step: prev, history }
      }

      case 'EDIT_STEP': {
        // Jump directly to a step (single-page "Change"); selections are kept
        // and reset naturally by the SELECT_* handlers if the user changes them.
        const steps = flowSteps(config, state)
        const idx = steps.indexOf(action.step)
        if (idx < 0) return state
        return { ...state, step: action.step, history: steps.slice(0, idx) }
      }

      case 'PRESELECT': {
        // Hydrate profile/status/region/zip from URL params (§2.4) and land on
        // the first step still missing a selection.
        const s: BookingState = { ...initialState, coords: state.coords, ...action.patch }
        const steps = flowSteps(config, s)
        let idx = 0
        while (idx < steps.length - 1 && stepFilled(s, steps[idx])) idx++
        return { ...s, step: steps[idx], history: steps.slice(0, idx) }
      }

      case 'RESTORE':
        return action.state

      case 'RESTART':
        return { ...initialState, coords: state.coords }

      default:
        return state
    }
  }
}
